import axios from 'axios';
import keycloak from './keycloak';
import { getApiBaseUrl } from './apiBaseUrl';
import { incomeBucket, isPlannedSavingsRow } from './budgetBuckets';

const API_BASE_URL = getApiBaseUrl();

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach Keycloak token. Refuse to send unauthenticated requests.
api.interceptors.request.use(
  async (config) => {
    if (!keycloak.authenticated) {
      return Promise.reject(new Error('Not authenticated'));
    }
    try {
      await keycloak.updateToken(30);
    } catch (err) {
      console.error('Token refresh failed before request:', err);
      return Promise.reject(err);
    }
    if (!keycloak.token) {
      return Promise.reject(new Error('No Keycloak token available'));
    }
    config.headers.Authorization = `Bearer ${keycloak.token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, force-refresh token once and retry. No auto-login redirect
// (would cause a reload storm if backend rejects the refreshed token too).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response && error.response.status;
    if (status === 401 && original && !original._retry401) {
      original._retry401 = true;
      try {
        await keycloak.updateToken(-1);
      } catch (refreshErr) {
        console.error('Token force-refresh failed after 401:', refreshErr);
        return Promise.reject(error);
      }
      if (keycloak.token) {
        original.headers.Authorization = `Bearer ${keycloak.token}`;
        return api.request(original);
      }
    }
    return Promise.reject(error);
  }
);

const apiCall = (method, url, data) => api[method](url, data);

// Expenses API
export const expenseAPI = {
  getAllExpenses: async () => {
    // Backend defaults to limit=1000 ordered by date DESC, which silently truncated
    // anything older than ~2 years for users with multi-year history. Bump to a
    // ceiling that covers Phil's full archive (2711 rows today, growing slowly).
    const response = await apiCall('get', '/expenses/?limit=20000');
    return response;
  },
  getExpenseChunk: async (skip, limit) => {
    // One block of the full list. The order (date DESC, id DESC) is stable,
    // so blocks stitch together without gaps or repeats.
    return apiCall('get', `/expenses/?skip=${skip}&limit=${limit}`);
  },
  getExpensesForPlan: async (planId) => {
    // Server-side filter on sonderausgaben_plan_id; the Sonderausgaben list
    // expands one plan at a time and needs only that plan's bookings.
    return apiCall('get', `/expenses/?limit=20000&sonderausgaben_plan_id=${planId}`);
  },
  getExpensesByCategory: async (code) => {
    // Server-side category filter; DepotTab wants the 'i' rows of the whole
    // history and nothing else.
    return apiCall('get', `/expenses/?limit=20000&category=${encodeURIComponent(code)}`);
  },
  getRecentExpenses: async (limit = 5) => {
    const response = await apiCall('get', `/expenses/?limit=${limit}`);
    return response;
  },
  getExpensesByMonth: async (year, month) => {
    // Server-side monthly filter. Preferred over getAllExpenses whenever the
    // caller only needs one month — payload drops from ~1-2 MB to a few KB
    // and Postgres uses the ix_expenses_user_id_date composite index.
    return apiCall('get', `/expenses/by-month/${year}/${month}`);
  },
  getExpensesByRange: async (fromYear, fromMonth, toYear, toMonth) => {
    // Ein Zeitraum, eine Anfrage. Ersetzt N Aufrufe von by-month; der Server
    // faehrt eine Bereichsabfrage ueber ix_expenses_user_id_date.
    return apiCall('get', `/expenses/by-range/${fromYear}/${fromMonth}/${toYear}/${toMonth}`);
  },
  getExpense: async (id) => {
    return apiCall('get', `/expenses/${id}`);
  },
  // No transform in either direction: `category` is the single-letter code
  // the backend stores, and the backend sends `category_name` for display.
  createExpense: (expense) => apiCall('post', '/expenses/', expense),
  updateExpense: (id, expense) => apiCall('put', `/expenses/${id}`, expense),
  deleteExpense: (id) => apiCall('delete', `/expenses/${id}`),
  // Massenbearbeitung: eine Anfrage, eine Transaktion. Bewusst kein Chunking --
  // das gaebe die Atomaritaet auf und damit die Grundlage fuer Rueckgaengig.
  bulkEdit: (payload) => apiCall('patch', '/expenses/bulk', payload),
  // Rueckgaengig: exakter Zustand pro Zeile. Braucht einen eigenen Endpunkt,
  // weil die markierten Zeilen verschiedene Vorzustaende gehabt haben koennen.
  bulkRestore: (rows) => apiCall('post', '/expenses/bulk/restore', { rows }),
};

// Invoices API
export const invoiceAPI = {
  uploadInvoice: (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/expenses/${expenseId}/invoices/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getInvoices: (expenseId) => apiCall('get', `/expenses/${expenseId}/invoices/`),
  downloadInvoice: (expenseId, invoiceId) =>
    api.get(`/expenses/${expenseId}/invoices/${invoiceId}/download`, {
      responseType: 'blob',
    }),
  deleteInvoice: (expenseId, invoiceId) => 
    apiCall('delete', `/invoices/${invoiceId}`),
};

// Analytics API
export const analyticsAPI = {
  getMonthlyTrends: async () => {
    const response = await apiCall('get', '/analytics/monthly-totals');
    return {
      ...response,
      data: response.data.map(item => ({
        month: `${item.month} ${item.year}`,
        total: item.total
      }))
    };
  },
  getSpendingTrends: async (startDate, endDate, granularity = 'month') => {
    const response = await apiCall('get', `/analytics/spending-trends?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`);
    return response;
  },
  getCategorySpendingTrends: async (startDate, endDate, granularity = 'month') => {
    const response = await apiCall('get', `/analytics/category-spending-trends?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`);
    return response;
  },
  getCategoryBreakdown: async () => {
    const response = await apiCall('get', '/analytics/category-spending');
    return {
      ...response,
      data: response.data.map(item => ({
        name: item.category_name,
        amount: Math.abs(item.total), // Absolute value for pie chart rendering
        originalValue: item.total // Keep original negative value for tooltip
      }))
    };
  },
  getCategoryBreakdownByRange: async (startDate, endDate) => {
    const response = await apiCall('get', `/analytics/category-spending-range?start_date=${startDate}&end_date=${endDate}`);
    return {
      ...response,
      data: response.data.map(item => ({
        name: item.category_name,
        amount: Math.abs(item.total), // Absolute value for pie chart rendering
        originalValue: item.total // Keep original negative value for tooltip
      }))
    };
  },
  getBiggestSpendingDays: () => apiCall('get', '/analytics/biggest-spending-days'),
  getBiggestSpendingDaysByRange: async (startDate, endDate) => apiCall('get', `/analytics/biggest-spending-days-by-date-range?start_date=${startDate}&end_date=${endDate}`),
  getDashboardSummary: async () => {
    // Combine multiple analytics endpoints to create dashboard summary
    try {
      const [monthlyTrends, categoryBreakdown, biggestDays] = await Promise.all([
        analyticsAPI.getMonthlyTrends(),
        analyticsAPI.getCategoryBreakdown(),
        analyticsAPI.getBiggestSpendingDays()
      ]);
      
      return {
        data: {
          monthlyTrends: monthlyTrends.data,
          categoryBreakdown: categoryBreakdown.data,
          biggestSpendingDays: biggestDays.data
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  },
};

export default api;

// ---------------------------------------------------------------------------
// Die Monatsrechnung, ein Mal implementiert.
//
// `actual` haengt an genau zwei Eingaben: den monthly_budget-Zeilen des Monats
// und den Ausgaben des Monats. An nichts sonst -- deshalb kann die
// Zeitraum-Ansicht dieselbe Rechnung auf Bereichsdaten fahren, statt die
// Monatsendpunkte N mal abzuklappern. Das Bucketing unten ist wortgleich das,
// was vorher inline in getBudgetVsActualAnalysis stand: Vorzeichen, die
// subcategory-Fallbacks, 'x' ausserhalb der Summen, taegliche 'e'-Zeilen als
// ungeplante Einnahme, 'i' in die Ersparnisse. Zwei Kopien davon waeren
// innerhalb eines Monats auseinandergelaufen.
// ---------------------------------------------------------------------------
const bucketMonthRows = (budgetRows, expenseRows) => {
  const monthlyExpenses = expenseRows || [];

  // Income is split into four buckets so the UI can show the breakdown and
  // tell plan from actual:
  //   - salary:         planned recurring wage
  //   - plannedOther:   every other grouped income line (Rente, Pflegegeld,
  //                     a second job) — planned, just not a payslip
  //   - unplannedOther: daily 'e' transactions and legacy rows without a
  //                     budget_group — actual only
  //   - correction:     manual reconciliation entries to match bank totals
  // Planned income = salary + plannedOther. It used to be salary alone,
  // which left anyone without a classic wage on a planned income of 0.
  const budgetCategories = {
    incomeSalary: 0,
    incomePlannedOther: 0,
    incomeUnplannedOther: 0,
    incomeCorrection: 0,
    deductions: 0,
    housing: 0,
    services: 0,        // laufende Kosten + Insurance
    loans: 0,           // student_loan, kitchen_loan, etc.
    sonderausgaben: 0,
    savings: 0,         // every planned savings line (Fixed Investment Plan)
    savingsOther: 0,    // savings_account deposits/withdrawals, stock_purchase
  };

  // Process budget data
  (budgetRows || []).forEach(budget => {
    const amount = parseFloat(budget.amount);
    const budgetGroup = budget.budget_group || null;

    // Bucketed by the structured fields, never by the subcategory slug —
    // the slug is derived from the name the user typed.
    const bucket = incomeBucket(budget);
    if (bucket) {
      if (bucket === 'salary') {
        budgetCategories.incomeSalary += amount;
      } else if (bucket === 'correction') {
        budgetCategories.incomeCorrection += amount;
      } else if (bucket === 'plannedOther') {
        budgetCategories.incomePlannedOther += amount;
      } else {
        budgetCategories.incomeUnplannedOther += amount;
      }
      return;
    } else if (budget.category_type === 'fixed_expense') {
      if (budgetGroup === 'deductions') {
        budgetCategories.deductions += amount;
        return;
      }

      if (budgetGroup === 'housing') {
        budgetCategories.housing += amount;
        return;
      }

      if (budgetGroup === 'services' || budgetGroup === 'insurance') {
        budgetCategories.services += amount;
        return;
      }

      if (budgetGroup === 'loans') {
        budgetCategories.loans += amount;
        return;
      }

      if (budgetGroup === 'sonderausgaben') {
        budgetCategories.sonderausgaben += amount;
        return;
      }

      if (budgetGroup === 'savings') {
        // Every savings line is part of the plan. It used to be the
        // `savings_plan` slug alone, so a savings plan the user created
        // themselves never reached the planned total — the slug is derived
        // from the typed name and can never be in a fixed list.
        // Sparkonto deposits and stock purchases stay out: they are
        // transfers between the user's own accounts, not a commitment, and
        // would distort the planned monthly investment.
        if (isPlannedSavingsRow(budget)) {
          budgetCategories.savings += amount;
        } else {
          budgetCategories.savingsOther += amount;
        }
        return;
      }

      // Categorize fixed expenses by subcategory
      switch (budget.subcategory) {
        // Deductions (taxes, insurance contributions)
        case 'income_tax':
        case 'unemployment_insurance':
        case 'pension_insurance':
        case 'solidarity_tax':
        case 'church_tax':
        case 'additional_employee_contribution':
        case 'vbl_insurance':
        case 'health_insurance':
          budgetCategories.deductions += amount; // Keep original sign (negative for expenses)
          break;
          
        // Housing
        case 'rent':
        case 'utilities':
        case 'electricity':
        case 'internet':
          budgetCategories.housing += amount; // Keep original sign (negative for expenses, positive for refunds)
          break;
          
        // Services (laufende Kosten) + Insurance
        case 'liability_insurance':
        case 'mobile':
        case 'mobile_babsch':
        case 'spotify':
        case 'netflix':
        case 'office_subscription':
        case 'broadcasting_fee':
        case 'humble_bundle':
        case 'crunchyroll':
        case 'wakanim':
        case 'bank_fees':
        case 'twitch':
        case 'discord':
        case 'amazon_prime':
        case 'copilot':
          budgetCategories.services += amount; // Keep original sign (negative for expenses, positive for refunds)
          break;
          
        // Savings (only count regular savings plan, not account transactions)
        case 'savings_plan':
          budgetCategories.savings += amount; // Keep original sign (negative for savings expenses)
          break;
          
        // Savings account transactions (treat as separate category, not part of regular savings plan)
        case 'savings_account':
          budgetCategories.savingsOther += amount;
          break;

        // Loans (student loan, kitchen loan, generic loan payments)
        case 'loan_payment':
        case 'student_loan':
        case 'kitchen_loan':
          budgetCategories.loans += amount;
          break;

        // Sonderausgaben fallback for items lacking a proper budget_group
        case 'university':
        case 'semester_ticket':
        case 'parking':
        case 'wealth_tax':
        case 'stock_purchase':
        case 'simp':
        case 'dsa':
          budgetCategories.sonderausgaben += amount; // Keep original sign (negative for expenses)
          break;
          
        default:
          // Uncategorized fixed expenses go to services
          budgetCategories.services += amount; // Keep original sign
          break;
      }
    }
  });
  // Actual daily spending per category code, self-created ones included.
  // The four system codes are handled explicitly; 'x' (legacy "not
  // assigned") stays out of the totals, exactly as the old switch left it.
  const dailyCategories = {};
  // Summen je Unterkategorie, fuer die optionale fuenfte Ebene im Sankey.
  // Schluessel ist die id, weil Namen Nutzerdaten sind; der Name reist mit,
  // damit die Anzeige keinen zweiten Abruf braucht. `unassigned` haelt den
  // Teil einer Hauptkategorie, der keiner Unterkategorie zugeordnet ist --
  // ohne ihn waere die Summe der Kinder kleiner als der Zufluss und das
  // Diagramm zeigte eine Luecke, die es nicht erklaert.
  const dailySubcategories = {};
  let actualSonderausgaben = 0;
  let actualInvestments = 0;

  monthlyExpenses.forEach(expense => {
    const amount = parseFloat(expense.amount); // Keep original sign (negative for expenses)
    const code = expense.category;

    if (code === 'z') {
      actualSonderausgaben += amount;
      return;
    }
    if (code === 'i') {
      // Investments flow into savings, not into daily expenses.
      actualInvestments += amount;
      return;
    }
    if (code === 'e') {
      // Daily 'e' rows (Twitch payouts, Modeling gigs, Patreon, Bonus,
      // etc.) are zusätzliche Einnahmen — without this branch the
      // BudgetSummaryCard renders them as 0 even though IncomeTab
      // correctly lists them, leaving Operativer Cashflow strictly
      // negative for users whose extras live in daily transactions.
      budgetCategories.incomeUnplannedOther += amount;
      return;
    }
    if (code === 'x') return;

    dailyCategories[code] = (dailyCategories[code] || 0) + amount;

    if (!dailySubcategories[code]) dailySubcategories[code] = { bySub: {}, unassigned: 0 };
    const bucket = dailySubcategories[code];
    if (expense.subcategory_id) {
      const key = String(expense.subcategory_id);
      if (!bucket.bySub[key]) {
        bucket.bySub[key] = { name: expense.subcategory_name || key, amount: 0 };
      }
      bucket.bySub[key].amount += amount;
    } else {
      bucket.unassigned += amount;
    }
  });
  return {
    budgetCategories,
    dailyCategories,
    dailySubcategories,
    actualSonderausgaben,
    actualInvestments,
  };
};

// Aus den Buckets der `actual`-Block. Die Budget-Seite bleibt beim Aufrufer:
// sie braucht zusaetzlich die variablen Budgets, die der Sankey nicht liest.
const actualFromBuckets = ({
  budgetCategories,
  dailyCategories,
  dailySubcategories,
  actualSonderausgaben,
  actualInvestments,
}) => {
  // Calculate totals for daily transactions
  const totalDailyTransactions = Object.values(dailyCategories)
    .reduce((sum, amount) => sum + amount, 0);

  // Additional Monthly Expenses come only from budget entries, not from daily transactions
  const totalAdditionalExpenses = budgetCategories.sonderausgaben;

  // Actual savings adds savings_account / stock_purchase entries plus daily 'i' transactions.
  const totalActualSavings = budgetCategories.savings
                           + budgetCategories.savingsOther
                           + actualInvestments;

  // Actual expenses (all negative values)
  const totalActualExpenses = budgetCategories.deductions +
                             budgetCategories.housing +
                             budgetCategories.services +
                             budgetCategories.loans +
                             totalAdditionalExpenses +
                             totalDailyTransactions +
                             actualSonderausgaben;

  // "Extras" on the actual side keeps its old meaning: planned non-salary
  // income plus whatever actually came in on top.
  const actualIncomeOther = budgetCategories.incomePlannedOther
                          + budgetCategories.incomeUnplannedOther;

  // Total actual income = salary + extras + correction.
  const totalActualIncome = budgetCategories.incomeSalary
                          + actualIncomeOther
                          + budgetCategories.incomeCorrection;

  return {
    income: totalActualIncome, // Salary + zusaetzliche Einnahmen + Korrektur
    incomeSalary: budgetCategories.incomeSalary,
    incomeOther: actualIncomeOther,
    incomeCorrection: budgetCategories.incomeCorrection,
    deductions: budgetCategories.deductions, // Same as budget (fixed)
    housing: budgetCategories.housing, // Same as budget (fixed)
    services: budgetCategories.services, // Same as budget (fixed)
    loans: budgetCategories.loans, // Same as budget (fixed)
    additionalExpenses: totalAdditionalExpenses, // Only from budget entries, not daily transactions
    sonderausgaben: actualSonderausgaben, // Actual Sonderausgaben from daily transactions (keep original sign)
    savings: totalActualSavings, // Budget savings + savings_account + investments
    dailyTransactions: {
      ...dailyCategories,
      total: totalDailyTransactions,
    },
    // Nur der Sankey liest das, und nur wenn die fuenfte Ebene eingeschaltet
    // ist. Die Struktur bleibt getrennt von dailyTransactions, damit dessen
    // Form (Code -> Zahl, plus total) unveraendert bleibt -- daran haengen
    // Dashboard und Budgetseite.
    dailySubcategories: dailySubcategories || {},
    totalExpenses: totalActualExpenses,
    totalSavings: totalActualSavings,
  };
};

// Eine Monatsrechnung, zwei Aufrufer: der Monatsendpunkt-Pfad
// (getBudgetVsActualAnalysis) und der Zeitraum-Pfad (getCashflowRange).
export const computeMonthlyActual = (budgetRows, expenseRows) =>
  actualFromBuckets(bucketMonthRows(budgetRows, expenseRows));

// Leerer Akkumulator und Monatsaddition fuer die Zeitraum-Ansicht. Stand
// vorher in CashFlowSankey; hierher gezogen, damit die Zeitraum-Abfrage
// dieselbe Addition benutzt wie vorher der Fan-out.
export const emptyActual = () => ({
  incomeSalary: 0, incomeOther: 0, incomeCorrection: 0,
  deductions: 0, housing: 0, services: 0, loans: 0,
  additionalExpenses: 0, sonderausgaben: 0, savings: 0,
  dailyTransactions: {
    // Filled per category code found in the data.
    total: 0,
  },
  dailySubcategories: {},
});

export const mergeActual = (acc, src) => {
  if (!src) return acc;
  acc.incomeSalary += Number(src.incomeSalary) || 0;
  acc.incomeOther += Number(src.incomeOther) || 0;
  acc.incomeCorrection += Number(src.incomeCorrection) || 0;
  acc.deductions += Number(src.deductions) || 0;
  acc.housing += Number(src.housing) || 0;
  acc.services += Number(src.services) || 0;
  acc.loans += Number(src.loans) || 0;
  acc.additionalExpenses += Number(src.additionalExpenses) || 0;
  acc.sonderausgaben += Number(src.sonderausgaben) || 0;
  acc.savings += Number(src.savings) || 0;
  if (src.dailyTransactions) {
    Object.entries(src.dailyTransactions).forEach(([code, amount]) => {
      acc.dailyTransactions[code] = (acc.dailyTransactions[code] || 0) + (Number(amount) || 0);
    });
  }
  if (src.dailySubcategories) {
    Object.entries(src.dailySubcategories).forEach(([code, bucket]) => {
      if (!acc.dailySubcategories[code]) {
        acc.dailySubcategories[code] = { bySub: {}, unassigned: 0 };
      }
      const ziel = acc.dailySubcategories[code];
      ziel.unassigned += Number(bucket.unassigned) || 0;
      Object.entries(bucket.bySub || {}).forEach(([key, sub]) => {
        if (!ziel.bySub[key]) ziel.bySub[key] = { name: sub.name, amount: 0 };
        ziel.bySub[key].amount += Number(sub.amount) || 0;
      });
    });
  }
  return acc;
};

// Budget API for Budget vs Actual comparison
export const budgetAPI = {
  // Get budget summary for a specific month
  getBudgetSummary: async (year, month) => {
    const response = await apiCall('get', `/budgets/summary/${year}/${month}`);
    return response;
  },
  
  // Get detailed monthly budget breakdown
  getMonthlyBudget: async (year, month) => {
    const response = await apiCall('get', `/budgets/monthly/${year}/${month}`);
    return response;
  },
  
  // Monatsbudgets eines ganzen Zeitraums. Die Zeilen tragen year und month,
  // also bleibt die Rechnung pro Monat moeglich.
  getMonthlyBudgetRange: async (fromYear, fromMonth, toYear, toMonth) => {
    return apiCall('get', `/budgets/monthly-range/${fromYear}/${fromMonth}/${toYear}/${toMonth}`);
  },

  // Die Zeitraum-Ansicht des Cash-Flow: zwei Anfragen, egal wieviele Monate.
  // Vorher waren es sechs pro Monat -- Jan 2020 bis Dez 2025 sind 432
  // Anfragen auf einen Schlag, daran ist erst der Browser und dann der
  // Verbindungspool gestorben. Gerechnet wird weiter pro Monat, mit
  // derselben Funktion wie im Monatspfad, und danach addiert.
  getCashflowRange: async (fromYear, fromMonth, toYear, toMonth) => {
    const [expensesResponse, budgetsResponse] = await Promise.all([
      expenseAPI.getExpensesByRange(fromYear, fromMonth, toYear, toMonth),
      budgetAPI.getMonthlyBudgetRange(fromYear, fromMonth, toYear, toMonth),
    ]);
    const budgetRows = Array.isArray(budgetsResponse?.data) ? budgetsResponse.data : [];
    const expenseRows = Array.isArray(expensesResponse?.data) ? expensesResponse.data : [];

    // Nach Monat sortieren, damit computeMonthlyActual dieselben Eingaben
    // sieht wie im Monatspfad. Budgetzeilen tragen year/month, Ausgaben ein
    // ISO-Datum -- beides ergibt denselben Schluessel.
    const perMonth = new Map();
    const slot = (key) => {
      if (!perMonth.has(key)) perMonth.set(key, { budgets: [], expenses: [] });
      return perMonth.get(key);
    };
    budgetRows.forEach((row) => {
      slot(`${row.year}-${String(row.month).padStart(2, '0')}`).budgets.push(row);
    });
    expenseRows.forEach((row) => {
      slot(String(row.date).slice(0, 7)).expenses.push(row);
    });

    const actual = emptyActual();
    perMonth.forEach(({ budgets, expenses }) => {
      mergeActual(actual, computeMonthlyActual(budgets, expenses));
    });

    // Die flache Budgetliste geht mit: der Sankey verteilt daraus die
    // Unterkategorien der Festausgaben, unveraendert zu vorher.
    return { actual, budgetEntries: budgetRows };
  },

  // Get comprehensive budget vs actual analysis with categorized data
  getBudgetVsActualAnalysis: async (year, month) => {
    try {
      // Five independent reads → fan them out. Payload for the last one is
      // one month's expenses (indexed range scan), not the full table.
      // Vier Aufrufe, nicht fuenf: /budgets/vs-actual wurde mitgeholt und
      // sein Ergebnis nie gelesen -- eine Anfrage je Monatsansicht, ueberall
      // in der App, fuer den Papierkorb. Der Endpunkt liefert ohnehin immer
      // eine leere Liste.
      const [
        budgetResponse,
        budgetSummaryResponse,
        variableExpenseBudgetsResponse,
        monthlyExpensesResponse,
      ] = await Promise.all([
        budgetAPI.getMonthlyBudget(year, month),
        budgetAPI.getBudgetSummary(year, month),
        budgetAPI.getCategoryBudgets(year, month),
        expenseAPI.getExpensesByMonth(year, month),
      ]);

      const budgetSummary = budgetSummaryResponse.data || {};
      const variableExpenseBudgets = variableExpenseBudgetsResponse.data || [];
      if (!Array.isArray(variableExpenseBudgets)) {
        throw new Error('Invalid budget data format');
      }
      const monthlyExpenses = monthlyExpensesResponse.data || [];


      // Keyed by category code, not by German name: names are user data and
      // self-created categories have no fixed key. Consumers read
      // budgetData.budget.dailyTransactions[code].
      // Nur die gespeicherten (negativen) Werte. Es gab daneben eine positiv
      // gedrehte Fassung "for display", die berechnet, summiert und nie
      // zurueckgegeben wurde.
      const dailyTransactionBudgetsActual = { total: 0 };
      
      // monthly_budgets.subcategory holds the category code for
      // variable_expense_budget rows (see alembic a009), so no name matching.
      // Not lowercased: codes are case-sensitive, 'a' and 'A' differ.
      variableExpenseBudgets.forEach(budget => {
        const code = budget.subcategory;
        dailyTransactionBudgetsActual[code] = budget.amount;
      });

      // Calculate total budget for daily transactions (using actual values for calculations)
      dailyTransactionBudgetsActual.total = Object.values(dailyTransactionBudgetsActual).reduce((sum, amount) => {
        return typeof amount === 'number' ? sum + amount : sum;
      }, 0) - dailyTransactionBudgetsActual.total; // Subtract total since it was included in the sum
      const monthBuckets = bucketMonthRows(budgetResponse.data, monthlyExpenses);
      const budgetCategories = monthBuckets.budgetCategories;
      const actual = actualFromBuckets(monthBuckets);

      // Fixed Investment Plan = every planned savings line.
      const totalBudgetSavings = budgetCategories.savings;
      const totalAdditionalExpenses = actual.additionalExpenses;
      const totalDailyTransactions = actual.dailyTransactions.total;

      // Calculate totals - keep original signs from database
      // Budget expenses (all negative values in database) + daily transaction budgets (actual negative values)
      const totalBudgetExpenses = budgetCategories.deductions +
                                 budgetCategories.housing +
                                 budgetCategories.services +
                                 budgetCategories.loans +
                                 budgetCategories.sonderausgaben +
                                 dailyTransactionBudgetsActual.total; // Use actual negative values from database

      // Planned income covers every grouped income line, salary or not.
      const plannedIncome = budgetCategories.incomeSalary
                          + budgetCategories.incomePlannedOther;

      // Monthly Net = Income + Expenses + Savings (expenses and savings are negative).
      // Net uses the actual income figure (includes corrections + extras).
      const monthlyNet = actual.income + actual.totalExpenses + actual.totalSavings;

      return {
        data: {
          year,
          month,
          // The raw month rows travel with the analysis so callers that need
          // both (DashboardLanding) do not fetch the same month twice.
          expenses: monthlyExpenses,
          budget: {
            income: plannedIncome, // Salary plus every other planned income line
            incomeSalary: budgetCategories.incomeSalary,
            incomeOther: budgetCategories.incomePlannedOther,
            incomeCorrection: 0, // Corrections are reconciliation, never planned
            deductions: budgetCategories.deductions,
            housing: budgetCategories.housing,
            services: budgetCategories.services,
            loans: budgetCategories.loans,
            additionalExpenses: budgetCategories.sonderausgaben,
            sonderausgaben: 0, // Budget doesn't have Sonderausgaben from daily transactions
            savings: totalBudgetSavings,
            dailyTransactions: {
              ...dailyTransactionBudgetsActual,
            },
            totalExpenses: totalBudgetExpenses,
            totalSavings: totalBudgetSavings
          },
          // Dieselbe Rechnung, die computeMonthlyActual der Zeitraum-Ansicht
          // liefert -- eine Implementierung, damit die Zahlen nicht driften.
          actual,
          summary: {
            budgetSummary: budgetSummary.data,
            monthlyNet: monthlyNet,
            totalDailyExpenses: totalDailyTransactions,
            totalAdditionalExpenses: totalAdditionalExpenses,
            variance: actual.income - actual.totalExpenses - (plannedIncome - totalBudgetExpenses)
          }
        }
      };
    } catch (error) {
      console.error('Error fetching budget vs actual analysis:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      throw error;
    }
  },
  
  // CRUD operations for budget management (for later use)
  createBudget: (budgetData) => apiCall('post', '/budgets/', budgetData),
  updateBudget: (budgetId, budgetData) => apiCall('put', `/budgets/${budgetId}`, budgetData),
  deleteBudget: (budgetId) => apiCall('delete', `/budgets/${budgetId}`),

  // Recurring budget items for fixed monthly/quarterly/etc. entries
  getRecurringBudgetItems: (year, month, includeAll = false) =>
    apiCall('get', `/budgets/recurring/${year}/${month}?include_all=${includeAll}`),
  createRecurringBudgetItem: (itemData, options = {}) =>
    apiCall('post', `/budgets/recurring${options.resolveOverlap ? '?resolve_overlap=true' : ''}`, itemData),
  updateRecurringBudgetItem: (itemId, itemData, options = {}) =>
    apiCall('put', `/budgets/recurring/${itemId}${options.resolveOverlap ? '?resolve_overlap=true' : ''}`, itemData),
  deleteRecurringBudgetItem: (itemId) => apiCall('delete', `/budgets/recurring/${itemId}`),
  setRecurringBudgetItemOverride: (itemId, year, month, adjustment) =>
    apiCall('put', `/budgets/recurring/${itemId}/overrides/${year}/${month}`, adjustment),
  clearRecurringBudgetItemOverride: (itemId, year, month) =>
    apiCall('delete', `/budgets/recurring/${itemId}/overrides/${year}/${month}`),

  // Category budget management
  getCategoryBudgets: async (year, month) => apiCall('get', `/budgets/categories/${year}/${month}`),
  setCategoryBudgets: (budgetData) => apiCall('post', '/budgets/categories/set', budgetData),
  bulkSetCategoryBudgets: (bulkData) => apiCall('post', '/budgets/categories/bulk-set', bulkData),

  // ==================== SONDERAUSGABEN PLANNING API ====================
  
  // Get all Sonderausgaben plans for a specific year
  getSonderausgabenPlans: async (year) => {
    try {
      const response = await apiCall('get', `/sonderausgaben-plans/${year}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Sonderausgaben plans:', error);
      throw error;
    }
  },

  // Get summary statistics for a year
  getSonderausgabenSummary: async (year) => {
    try {
      const response = await apiCall('get', `/sonderausgaben-plans/${year}/summary`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Sonderausgaben summary:', error);
      throw error;
    }
  },

  // Get a specific plan by ID
  getSonderausgabenPlan: async (planId) => {
    try {
      const response = await apiCall('get', `/sonderausgaben-plans/plan/${planId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Sonderausgaben plan:', error);
      throw error;
    }
  },

  // Create a new Sonderausgaben plan
  createSonderausgabenPlan: async (planData) => {
    try {
      const response = await apiCall('post', '/sonderausgaben-plans', planData);
      return response.data;
    } catch (error) {
      console.error('Error creating Sonderausgaben plan:', error);
      throw error;
    }
  },

  // Update an existing Sonderausgaben plan
  updateSonderausgabenPlan: async (planId, updateData) => {
    try {
      const response = await apiCall('put', `/sonderausgaben-plans/${planId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating Sonderausgaben plan:', error);
      throw error;
    }
  },

  // Delete a Sonderausgaben plan
  deleteSonderausgabenPlan: async (planId) => {
    try {
      const response = await apiCall('delete', `/sonderausgaben-plans/${planId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting Sonderausgaben plan:', error);
      throw error;
    }
  },

  // Get available planning years
  getSonderausgabenYears: async () => {
    try {
      const response = await apiCall('get', '/sonderausgaben-plans/years');
      return response.data;
    } catch (error) {
      console.error('Error fetching planning years:', error);
      throw error;
    }
  },

  // Get plans that can be linked to expenses
  getLinkablePlans: async (year) => {
    try {
      const response = await apiCall('get', `/expenses/linkable-plans/${year}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching linkable plans:', error);
      throw error;
    }
  },

  // Link an expense to a plan
  linkExpenseToPlan: async (expenseId, planId) => {
    try {
      const response = await apiCall('put', `/expenses/${expenseId}/link-plan`, { plan_id: planId });
      return response.data;
    } catch (error) {
      console.error('Error linking expense to plan:', error);
      throw error;
    }
  },

  // Unlink an expense from a plan
  unlinkExpenseFromPlan: async (expenseId) => {
    try {
      const response = await apiCall('put', `/expenses/${expenseId}/unlink-plan`);
      return response.data;
    } catch (error) {
      console.error('Error unlinking expense from plan:', error);
      throw error;
    }
  },

  // Get budget variance alerts and recommendations
  getBudgetAlerts: async (year) => {
    try {
      const response = await apiCall('get', `/sonderausgaben-plans/${year}/alerts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching budget alerts:', error);
      throw error;
    }
  },

  // Get monthly Sonderausgaben budget based on target months
  getSonderausgabenMonthlyBudget: async (year, month) => {
    try {
      const response = await apiCall('get', `/sonderausgaben-plans/${year}/monthly-budget/${month}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching monthly budget:', error);
      throw error;
    }
  }
};

// Tags haben absichtlich keine Verwaltung: sie entstehen beim Speichern einer
// Ausgabe und vergehen mit der letzten Verwendung. Deshalb nur Lesen.
export const tagAPI = {
  list: () => apiCall('get', '/tags'),
};

// Depot Snapshots API.
// Bypasses `apiCall()` deliberately — `apiCall` swallows HTTP errors and falls
// back to mock data, which would hide the 409 "snapshot for this date already
// exists" response from the UI. We need that error to propagate so the modal /
// inline-edit row can show a German error message.
export const depotAPI = {
  list: ({ from, to } = {}) => api.get('/depot/snapshots/', { params: { from, to } }),
  create: (data) => api.post('/depot/snapshots/', data),
  update: (id, data) => api.put(`/depot/snapshots/${id}`, data),
  remove: (id) => api.delete(`/depot/snapshots/${id}`),
};

// Liquid Snapshots API — Konten (bank) + Bargeld (cash) Bestände.
// Uses the exported `api` axios instance directly (NOT `apiCall`) so that
// HTTP errors propagate and the UI can react to 409 (duplicate snapshot for
// month/type) by switching to PUT instead of falling back to mock data.
export const liquidAPI = {
  list: ({ from, to, type } = {}) =>
    api.get('/liquid/snapshots/', { params: { from, to, type } }),
  create: (data) => api.post('/liquid/snapshots/', data),
  update: (id, data) => api.put(`/liquid/snapshots/${id}`, data),
  remove: (id) => api.delete(`/liquid/snapshots/${id}`),
};

// Category management. Names and structure are user data, so every consumer
// goes through CategoryContext rather than a static mapping.
export const categoryApi = {
  list: async (includeInactive = false) => {
    const query = includeInactive ? '?include_inactive=true' : '';
    const response = await apiCall('get', `/categories${query}`);
    return response.data;
  },
  create: async (payload) => {
    const response = await apiCall('post', '/categories', payload);
    return response.data;
  },
  update: async (id, payload) => {
    const response = await api.patch(`/categories/${id}`, payload);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/categories/${id}`);
  },
  createSubcategory: async (categoryId, payload) => {
    const response = await apiCall('post', `/categories/${categoryId}/subcategories`, payload);
    return response.data;
  },
  updateSubcategory: async (id, payload) => {
    const response = await api.patch(`/subcategories/${id}`, payload);
    return response.data;
  },
  removeSubcategory: async (id) => {
    await api.delete(`/subcategories/${id}`);
  },
};
